import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Copy, QrCode, Plus, Archive, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { downloadQr } from './pesoShare';

const TYPES = [['sra', 'SRA'], ['lra', 'LRA'], ['job_fair', 'Job Fair'], ['other', 'Other']];

const EventShareRow = ({ label, url, file }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t('peso.linkCopied', 'Link copied')); }
    catch { toast.error(t('peso.copyFailed', 'Could not copy — select the link and copy manually.')); }
  };
  const dl = async () => {
    setBusy(true);
    try { await downloadQr(url, file); toast.success(t('peso.qrDownloaded', 'QR code downloaded')); }
    catch { toast.error(t('peso.qrFailed', 'Could not generate the QR code. Please try again.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        <div className="text-[11px] text-slate-500 truncate font-mono">{url}</div>
      </div>
      <Button size="sm" variant="outline" onClick={copy}><Copy size={14} className="mr-1" /> {t('peso.copy', 'Copy')}</Button>
      <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={dl}>
        {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <QrCode size={14} className="mr-1" />} {t('peso.downloadQr', 'QR code')}
      </Button>
    </div>
  );
};

// events: array from GET /api/peso/events; onReload: refetch; api/authHeaders: passed from dashboard; demo: bool
export default function PesoEventsTab({ events, onReload, api, authHeaders, demo }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', event_type: 'sra', venue: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');

  const create = async () => {
    if (!form.name.trim() || !form.start_date) { toast.error(t('peso.eventNameDateReq', 'Event name and start date are required.')); return; }
    if (demo) { toast.info(t('peso.demoNoWrite', 'Demo mode — creating events is disabled.')); return; }
    setSaving(true);
    try {
      const headers = await authHeaders();
      await api.post('/api/peso/events', form, { headers });
      setForm({ name: '', event_type: 'sra', venue: '', start_date: '', end_date: '' });
      toast.success(t('peso.eventCreated', 'Event created'));
      onReload();
    } catch (e) { toast.error(t('peso.eventCreateFailed', 'Could not create the event.')); }
    finally { setSaving(false); }
  };

  const archive = async (ev) => {
    if (demo) { toast.info(t('peso.demoNoWrite', 'Demo mode — creating events is disabled.')); return; }
    setBusyId(ev.id);
    try {
      const headers = await authHeaders();
      await api.patch(`/api/peso/events/${ev.id}`, { status: ev.status === 'active' ? 'archived' : 'active' }, { headers });
      onReload();
    } catch (e) { toast.error(t('peso.eventUpdateFailed', 'Could not update the event.')); }
    finally { setBusyId(''); }
  };

  return (
    <div className="space-y-4">
      {/* Spec §9 link-only tradeoff — make the venue-QR requirement explicit to staff. */}
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
        {t('peso.eventLinkOnlyHint', 'Everyone at the venue must register through this event’s QR / link to be counted under it. Walk-ins who use the generic office link are not tagged to the event.')}
      </p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus size={15} className="text-blue-700" /> {t('peso.newEvent', 'New event (SRA / job fair)')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder={t('peso.eventName', 'Event name')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.event_type} onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{t(`peso.type_${v}`, l)}</option>)}
            </select>
            <input className="rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder={t('peso.eventVenue', 'Venue (optional)')} value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} />
            <div className="flex gap-2 flex-wrap">
              <input type="date" className="rounded-md border border-input bg-background px-2 py-2 text-sm flex-1 min-w-0" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
              <input type="date" className="rounded-md border border-input bg-background px-2 py-2 text-sm flex-1 min-w-0" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={saving} onClick={create}>
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />} {t('peso.createEvent', 'Create event')}
          </Button>
        </CardContent>
      </Card>

      {(events || []).length === 0 && (
        <p className="text-sm text-muted-foreground px-1">{t('peso.noEvents', 'No events yet. Create one to generate shareable registration links.')}</p>
      )}

      {(events || []).map((ev) => (
        <Card key={ev.id} className={ev.status !== 'active' ? 'opacity-60' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <CalendarDays size={15} className="text-blue-700" /> {ev.name}
              <Badge variant="outline" className="text-[10px]">{t(`peso.type_${ev.event_type}`, ev.event_type)}</Badge>
              {ev.status !== 'active' && <Badge variant="secondary" className="text-[10px]">{t('peso.archived', 'Archived')}</Badge>}
            </CardTitle>
            <p className="text-[11px] text-slate-400">
              {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ''}
              {ev.venue ? ` · ${ev.venue}` : ''} · {t('peso.seekersShort', 'seekers')}: {ev.seeker_count} · {t('peso.employersShort', 'employers')}: {ev.employer_count}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <EventShareRow label={t('peso.shareSeeker', 'Job Seeker Registration')} url={ev.seeker_url} file={`PESO_${ev.code}_JobSeeker_QR.png`} />
            <EventShareRow label={t('peso.shareEmployer', 'Employer Registration')} url={ev.employer_url} file={`PESO_${ev.code}_Employer_QR.png`} />
            <Button size="sm" variant="outline" disabled={busyId === ev.id} onClick={() => archive(ev)}>
              {busyId === ev.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Archive size={14} className="mr-1" />}
              {ev.status === 'active' ? t('peso.archive', 'Archive') : t('peso.unarchive', 'Unarchive')}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
